/**
 * L'AZIENDA IN BACHECA — sezione `board` del suo `EntityTypeDescriptor` (TS-117).
 *
 * È il gemello di `personBoard`, `taskBoard` e `quotationBoard`, e la somiglianza è il punto:
 * mettere un'entità in bacheca è scrivere **questo file** e registrarlo. Niente colonne, niente DDL,
 * niente pagina dedicata — la pagina della bacheca non conosce l'azienda.
 *
 * Le aziende si caricano dal REST **dell'azienda** con `?kanbanId=`: è il server a risolvere quali
 * appartengono alla board, e il DTO che torna è quello pieno, così la card ha settore, forma
 * giuridica e recapiti senza chiedere altro.
 *
 * <b>`currentStateId` non è un campo dell'azienda a database</b>, è una riga di
 * `PipelineMembership` che il server risolve prima di rispondere. E non è lo `status`
 * (attiva/inattiva): sono due assi indipendenti, e la stessa azienda può stare in pipeline diverse
 * con stati diversi.
 *
 * `businessApi` si importa **dinamicamente**, come negli altri board e come fanno `resolveLabel` e
 * `search` nel registro: il modulo api non deve finire nel percorso di boot, altrimenti si riapre il
 * ciclo di import che `coreEntityRegistration` evita per costruzione. La card invece è un import
 * statico: disegna e basta, e non torna indietro al registro.
 */

import type { EntityTypeDescriptor, Page } from '@crm/contracts';
import { BusinessKanbanCard } from './BusinessKanbanCard';
import type { Business } from './businessMappers';

type BoardSection = NonNullable<EntityTypeDescriptor['board']>;

/** Il descrittore parla di record generici; qui sotto sono aziende, e il cast sta tutto qui. */
const asBusiness = (item: Record<string, unknown>) => item as unknown as Business;

export const businessBoard: BoardSection = {
  // pageSize alto e non paginazione: la bacheca mostra tutte le colonne insieme, e una card mancante
  // non si distingue da una card filtrata via.
  loadItems: async (kanbanId, groupId) => {
    const { businessApi } = await import('./businessApi');
    const page = await businessApi.getAll(groupId, { kanbanId, pageSize: 1000 });

    return page as unknown as Page<Record<string, unknown>>;
  },

  idOf: (item) => asBusiness(item).businessId,

  stateIdOf: (item) => asBusiness(item).currentStateId ?? 0,

  move: async (id, pipelineStateId) => {
    const { businessApi } = await import('./businessApi');

    await businessApi.update({ businessId: id, currentStateId: pipelineStateId });
  },

  Card: ({ item }) => <BusinessKanbanCard business={asBusiness(item)} />,
};
